import React, { useMemo } from 'react';
import _get from 'lodash/get';
import _sortBy from 'lodash/sortBy';
import { makeStyles } from '@material-ui/core/styles';
import useEntityTypes from 'helpers/useEntityTypes';
import * as Sift from '@sift/types';
import { Fields } from 'types/FormValue';

// Components
import { arrayMove } from 'react-sortable-hoc';
import SortableList from './SortableList';
import AttributeSelector from 'components/attributeSelector/Local';

export interface LayoutItem {
  type: 'field' | 'category';
  name: string;
}

export type Layout = LayoutItem[];

interface Props {
  allFields: Sift.Field[];
  layout: Layout;
  onChange: (layout: Layout) => void;
  useAttributeDropdown?: boolean;
  fields: Fields;
  typeKey: string;
}

export default function GenericLayoutBuilder({
  allFields,
  layout,
  onChange,
  useAttributeDropdown = true,
  fields,
  typeKey,
}: Props) {
  const classes = useStyles();
  const entityTypes = useEntityTypes();
  const entityType = _get(entityTypes, typeKey);

  function formatConfigOptionDisplayName(attribute: any) {
    const keyedFields = _get(entityType, '_fields');
    if (attribute.objectKey.includes('.')) {
      const [rootParentKey] = attribute.objectKey.split('.');
      const rootParentField = keyedFields[rootParentKey];

      // For now, we're only changing display for reachOutToMeFor
      if (_get(rootParentField, 'objectKey') === 'reachOutToMeFor') {
        return `${rootParentField.displayName} - ${attribute.displayName}`;
      }
    }

    return attribute.displayName;
  }

  const attributes = useMemo(() => {
    const filteredFields = layout.reduce(
      (acc, item) => {
        if (item.type !== 'field') return acc;

        delete acc[item.name];

        return acc;
      },
      { ...fields },
    );

    const filteredFieldsCollection = Object.keys(filteredFields).map(key => {
      let newField = filteredFields[key] as any;
      if (newField.objectKey.includes('.')) {
        newField.newDisplayName = formatConfigOptionDisplayName(newField);
      } else {
        newField.newDisplayName = newField.displayName;
      }

      return newField;
    });

    const fieldsAlphabeticallyOrdered = _sortBy(filteredFieldsCollection, ['newDisplayName']);
    return fieldsAlphabeticallyOrdered;
  }, [layout, fields]) as any;

  function handleRemoveItem(index: number) {
    const newLayout = [...layout];
    newLayout.splice(index, 1);
    onChange(newLayout);
  }

  function handleSaveAttributes(fields: any) {
    const newFields = Object.keys(fields || []).map(key => ({
      name: key,
      type: 'field',
    })) as LayoutItem[];
    const newLayout = [...newFields, ...layout];
    onChange(newLayout);
  }

  function handleSortEnd({ oldIndex, newIndex }: any) {
    const newLayout = arrayMove(layout, oldIndex, newIndex);
    onChange(newLayout);
  }

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        {useAttributeDropdown && (
          <AttributeSelector
            label="Add Attributes"
            title="Add Profile Attributes"
            searchPlaceholder="Search attributes"
            attributes={attributes}
            selectedAttributes={{}}
            onSaveSelectedAttributes={handleSaveAttributes}
            getAttributeKey={getAttributeKey}
            getAttributeDisplayName={formatConfigOptionDisplayName}
            LabelButtonProps={{
              children: 'Update',
            }}
            saveButtonLabel={'Add'}
          />
        )}
      </div>
      <div className={classes.content}>
        <SortableList
          classes={classes}
          fields={allFields}
          layout={layout}
          useDragHandle
          onLayoutChange={handleSortEnd}
          onRemoveItem={!!useAttributeDropdown ? handleRemoveItem : undefined}
          onSortEnd={handleSortEnd}
        />
      </div>
    </div>
  );
}

function getAttributeKey(attribute: any) {
  return attribute.objectKey;
}

const useStyles = makeStyles((theme: any) => {
  return {
    root: {},
    header: {
      display: 'flex',
      justifyContent: 'flex-end',
      borderRadius: 5,
      marginBottom: theme.spacing(2),
    },
    content: {
      margin: 'auto',
      width: '100%',
    },
  };
});
